import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { hydrateUser } from './utils/authUser';
import { apiClient, getStoredActiveCustomerId, setStoredActiveCustomerId } from './services/api';

// Components
import Login from './components/Login';
import Callback from './components/Callback';
import Dashboard from './components/Dashboard';
import Instances from './components/Instances';
import InstanceDetail from './components/InstanceDetail';
import NewInstance from './components/NewInstance';
import Clusters from './components/Clusters';
import NewCluster from './components/NewCluster';
import ClusterDetail from './components/ClusterDetail';
import Usage from './components/Usage';
import Settings from './components/Settings';
import Layout from './components/Layout';

// Auth context
export const AuthContext = React.createContext(null);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [activeCustomerId, setActiveCustomerId] = useState('');
  const [customerScopeLoading, setCustomerScopeLoading] = useState(false);
  const location = useLocation();
  const hasBindingToken =
    location.pathname === '/login' &&
    new URLSearchParams(location.search).has('binding_token');

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (!user) {
      setCustomerOptions([]);
      setActiveCustomerId('');
      setStoredActiveCustomerId('');
      return;
    }

    refreshCustomerScope(user);
  }, [user]);

  const checkAuth = async () => {
    try {
      const currentUser = await Auth.currentAuthenticatedUser({ bypassCache: true });
      const hydratedUser = await hydrateUser(currentUser);
      setUser(hydratedUser);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await Auth.signOut();
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const refreshCustomerScope = async (currentUser = user) => {
    if (!currentUser) {
      return;
    }

    setCustomerScopeLoading(true);
    try {
      const response = await apiClient.get('/customers');
      const customers = Array.isArray(response.data?.customers) ? response.data.customers : [];
      const customerIds = new Set(customers.map((entry) => entry.customer_id).filter(Boolean));
      const claimCustomerId = currentUser?.attributes?.['custom:customer_id'] || '';
      const storedCustomerId = getStoredActiveCustomerId();

      let selectedCustomerId = response.data?.selected_customer_id || '';
      if (!selectedCustomerId && storedCustomerId && customerIds.has(storedCustomerId)) {
        selectedCustomerId = storedCustomerId;
      }
      if (!selectedCustomerId && claimCustomerId && customerIds.has(claimCustomerId)) {
        selectedCustomerId = claimCustomerId;
      }
      if (!selectedCustomerId && customers.length > 0) {
        selectedCustomerId = customers[0].customer_id;
      }

      setCustomerOptions(customers);
      setActiveCustomerId(selectedCustomerId);
      setStoredActiveCustomerId(selectedCustomerId);
    } catch (error) {
      const claimCustomerId = currentUser?.attributes?.['custom:customer_id'] || '';
      if (claimCustomerId) {
        setCustomerOptions([{ customer_id: claimCustomerId }]);
      } else {
        setCustomerOptions([]);
      }
      setActiveCustomerId(claimCustomerId);
      setStoredActiveCustomerId(claimCustomerId);
    } finally {
      setCustomerScopeLoading(false);
    }
  };

  const selectCustomerId = (customerId) => {
    const normalized = customerId || '';
    setActiveCustomerId(normalized);
    setStoredActiveCustomerId(normalized);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        signOut,
        checkAuth,
        customerOptions,
        activeCustomerId,
        customerScopeLoading,
        refreshCustomerScope,
        selectCustomerId
      }}
    >
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={user && !hasBindingToken ? <Navigate to="/" /> : <Login />} />
        <Route path="/callback" element={<Callback />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={user ? <Layout /> : <Navigate to="/login" />}
        >
          <Route index element={<Dashboard />} />
          <Route path="instances" element={<Instances />} />
          <Route path="instances/new" element={<NewInstance />} />
          <Route path="instances/:instanceId" element={<InstanceDetail />} />
          <Route path="clusters" element={<Clusters />} />
          <Route path="clusters/new" element={<NewCluster />} />
          <Route path="clusters/:clusterId" element={<ClusterDetail />} />
          <Route path="usage" element={<Usage />} />
          <Route path="settings" element={<Settings />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </AuthContext.Provider>
  );
}

export default App;
